/* eslint-disable indent */
/* eslint-disable complexity */
import { inject, Injectable } from '@angular/core';
import { LegView } from '@sequence/models/view/leg.view';
import { RequestState, RequestStatus } from '@shared/models/request-state';
import { DecsService } from '@shared/services/decs/decs.service';
import { combineLatest, map, Observable } from 'rxjs';
import { SeatMapRow, SeatMapView, SeatType } from './models/seat-map.view';
import { AlertService } from '@shared/services/alert/alert.service';
import { DecsResponse } from '@shared/services/decs/models/response/decs.response';
import { DateTime } from 'luxon';

interface SeatColumn {
  seat: string;
  index: number;
}

@Injectable({
  providedIn: 'root',
})
export class SeatMapService {
  decsService = inject(DecsService);
  alertService = inject(AlertService);

  getSeatMap(
    leg: LegView,
    isRefresh = false,
  ): Observable<RequestState<SeatMapView>> {
    // Different commands for first, business, premium economy and economy.
    // Only widebodies use all four classes.
    const date = leg.depTimeSched?.toFormat('ddMMM');
    const firstCmd = `G*M${leg.flightNumber}F${date}${leg.depStation}${leg.arrStation}`;
    const businessCmd = `G*M${leg.flightNumber}C${date}${leg.depStation}${leg.arrStation}`;
    const premEconCmd = `G*M${leg.flightNumber}W${date}${leg.depStation}${leg.arrStation}`;
    const econCmd = `G*M${leg.flightNumber}Y${date}${leg.depStation}${leg.arrStation}`;
    const requests: Observable<RequestState<DecsResponse>>[] = [];
    let isWidebody = false;
    let hasFirstClass = false;

    // Classes
    // B777-200    C W Y
    // B777-300  F C W Y
    // B787-8      C W Y
    // B787-9      C W Y
    // A32F/B737 F     Y
    // A321-T    F C   Y
    // A321NY    F   W Y

    if (
      leg.aircraft.short.startsWith('B787') ||
      leg.aircraft.short.startsWith('B777')
    ) {
      isWidebody = true;

      if (leg.aircraft.short === 'B777-300') {
        hasFirstClass = true;
      }
    } else {
      hasFirstClass = true;
    }

    if (hasFirstClass) {
      requests.push(
        this.decsService.submitResRequest({ cmd: firstCmd }, isRefresh),
      );
    }

    if (leg.aircraft.short === 'A321-T') {
      requests.push(
        this.decsService.submitResRequest({ cmd: businessCmd }, isRefresh),
      );
    }

    if (leg.aircraft.short === 'A321NY') {
      requests.push(
        this.decsService.submitResRequest({ cmd: premEconCmd }, isRefresh),
      );
    }

    if (isWidebody) {
      requests.push(
        this.decsService.submitResRequest({ cmd: businessCmd }, isRefresh),
      );
      requests.push(
        this.decsService.submitResRequest({ cmd: premEconCmd }, isRefresh),
      );
    }

    requests.push(
      this.decsService.submitResRequest({ cmd: econCmd }, isRefresh),
    );

    return combineLatest(requests).pipe(
      map((responses) => {
        // console.log(responses);

        let isLoading = false;

        // If any response is still loading or has an error, return loading or error state.
        for (const res of responses) {
          if (res.isLoading) {
            isLoading = true;
          }
          if (res.hasError) {
            this.showErrorAlert();
            return new RequestState<SeatMapView>(
              RequestStatus.HasErrorNoResponse,
            );
          }
        }

        if (isLoading) {
          return new RequestState<SeatMapView>(
            RequestStatus.IsLoadingNoResponse,
          );
        }

        let rows: SeatMapRow[] = [];

        for (const response of responses) {
          const sectionRows = this.parseSeatMap(
            response.response?.result || '',
            leg.aircraft.short,
          );
          let sectionFound = false;

          // Check that this section doesn't already exist to prevent duplicates.
          if (sectionRows.rows.length > 0) {
            const sectionClass = sectionRows.rows[0].sectionLabel;

            for (const row of rows) {
              if (row.sectionLabel === sectionClass) {
                sectionFound = true;
              }
            }
          }

          if (!sectionFound) {
            rows = rows.concat(sectionRows.rows);
          }
        }

        this.shiftSeats(rows);

        return new RequestState<SeatMapView>(
          RequestStatus.IsCompleteWithResponse,
          {
            timestamp: DateTime.fromISO(responses[0].response?.timestamp || ''),
            rows,
          },
        );
      }),
    );
  }

  private parseSeatMap(text: string, aircraft: string): SeatMapView {
    let bodySection = false; // Flag to indicate we are past the header portion.
    let columnIndices: SeatColumn[] = [];
    let sectionLabel = '';
    const rows: SeatMapRow[] = [];

    for (const line of text.split('\n')) {
      if (line.startsWith(' AA') && line.length > 20) {
        const sectionClass = line.slice(16, 17);

        switch (sectionClass) {
          case 'F':
            sectionLabel = 'First';
            break;
          case 'C':
            // Even though the Sabre response shows 'C', it's labeled First Class.
            if (
              aircraft !== 'A321-T' &&
              (aircraft.startsWith('A3') || aircraft.startsWith('B737'))
            ) {
              sectionLabel = 'First';
            } else {
              sectionLabel = 'Business';
            }
            break;
          case 'W':
            sectionLabel = 'Prem Economy';
            break;
          case 'Y':
            sectionLabel = 'Main';
            break;
          default:
            break;
        }

        rows.push({ sectionLabel, seats: [] });
      }

      if (line.startsWith(' NO SMOKING')) {
        // console.log('Found body');
        bodySection = true;
        continue;
      }

      if (!bodySection) {
        continue;
      }

      // Seat column letters
      if (line.startsWith('      A')) {
        columnIndices = this.getSeatColumns(line);
        continue;
      }

      const rowNumText = line.slice(2, 4).trim();
      let rowNumber: number | undefined;
      let isExitRow = false;

      if (rowNumText.match(/\d{1,2}/)) {
        rowNumber = parseInt(rowNumText);

        if (line.slice(4, 5) === 'X') {
          isExitRow = true;
        }
      }

      const seatMapRow: SeatMapRow = {
        rowNumber,
        seats: [],
        isExitRow,
      };

      let isSeatRow = false;

      for (const col of columnIndices) {
        let seatType: SeatType;

        if (col.seat === 'aisle') {
          seatType = SeatType.Aisle;
        } else {
          const seatText = line.slice(col.index, col.index + 3).trim();

          // Determine seat seatType
          switch (seatText) {
            case 'BHD':
              seatType = SeatType.Bulkhead;
              seatMapRow.isBulkhead = true;
              break;
            case 'DOR':
              seatType = SeatType.Door;
              break;
            case 'GAL':
              seatType = SeatType.Galley;
              break;
            case 'LAV':
              seatType = SeatType.Lav;
              break;
            case 'PAL':
              seatType = SeatType.Lav;
              break;
            case '':
              seatType = SeatType.Blank;
              break;
            default:
              // Placeholder - open/assigned determined later.
              seatType = SeatType.Assigned;
              isSeatRow = true;
              break;
          }

          // Determine assigned/open.
          if (seatType === SeatType.Assigned && seatText.startsWith('*')) {
            seatType = SeatType.Open;
          }
        }

        seatMapRow.seats.push({
          seatLetter: col.seat,
          seatType,
        });
      }

      // Initially planned to display galley and lav positions but it has proven problematic.
      // For now, keep rows that contain bulkheads or seats.

      if (seatMapRow.isBulkhead || isSeatRow) {
        rows.push(seatMapRow);
      }
    }

    // For some reason, seat 10A on the A321NY doesn't show up in the data.
    // Create a note row to display.
    if (aircraft === 'A321NY' && sectionLabel === 'First') {
      rows.push({
        note: 'Seat 10A contains no data for unknown reasons.',
        seats: [],
      });
    }

    return {
      rows,
    };
  }

  private getSeatColumns(line: string): SeatColumn[] {
    let columnIndices: SeatColumn[] = [];

    for (let i = 0; i < line.length; i++) {
      const char = line.charAt(i);
      if (char !== ' ') {
        // console.log('Seat columns');
        // console.log(`Found ${char} at ${i}`);
        columnIndices.push({
          seat: char,
          index: i,
        });
      }
    }

    // Find aisles
    const aisles: SeatColumn[] = [];

    for (let i = 1; i < columnIndices.length; i++) {
      if (columnIndices[i].index - columnIndices[i - 1].index > 4) {
        aisles.push({
          seat: 'aisle',
          index: columnIndices[i].index - 4,
        });
      }
    }

    columnIndices = columnIndices
      .concat(aisles)
      .sort((a, b) => a.index - b.index);

    // console.log(columnIndices);

    return columnIndices;
  }

  // In cases where there is a blank space between two seats
  // the Sabre text will display something like this:
  //  A  B  C
  // []    []
  // The inboard seat needs shifted outboard:
  // [] []
  private shiftSeats(rows: SeatMapRow[]): void {
    // Detect if correction needed.
    for (const row of rows) {
      for (let i = 0; i <= row.seats.length - 3; i++) {
        // Look for seat - blank - seat pattern.
        if (
          (row.seats[i].seatType === SeatType.Open ||
            row.seats[i].seatType === SeatType.Assigned) &&
          row.seats[i + 1].seatType === SeatType.Blank &&
          (row.seats[i + 2].seatType === SeatType.Open ||
            row.seats[i + 2].seatType === SeatType.Assigned)
        ) {
          // Determine whether to shift left or right. Needs to shift outboard.
          // Easiest case is when this group is at start or end of array.
          // console.log('Shift needed', row, i);

          if (i === 0) {
            row.seats[i + 1].seatType = row.seats[i + 2].seatType;
            row.seats[i + 2].seatType = SeatType.Blank;
          } else if (i === row.seats.length - 3) {
            row.seats[row.seats.length - 2].seatType =
              row.seats[row.seats.length - 3].seatType;
            row.seats[row.seats.length - 3].seatType = SeatType.Blank;
          }
        }
      }
    }
  }

  private showErrorAlert(): void {
    this.alertService.setAlert({
      type: 'error',
      message: 'Unable to get seat map information.',
    });
  }
}
